
import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import MainLayout from "./components/Layout/MainLayout";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import PlanGate from "./components/Plan/PlanGate";
// Import NotFound directly to avoid dynamic import issues in error boundaries
import NotFound from "./pages/NotFound";

// Import Uppdrag directly to avoid React duplication issues
import Uppdrag from "./pages/Uppdrag";

// Lazy-loaded pages for better code-splitting
const Riskbedomning = lazy(() => import("./pages/Riskbedomning"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Home = lazy(() => import("./pages/Home"));
const EditProjectPage = lazy(() => import("./pages/EditProject"));
const DocumentSettingsPage = lazy(() => import("./pages/DocumentSettings"));
const TranslationSettings = lazy(() => import("./pages/TranslationSettings"));
const MyDocuments = lazy(() => import("./pages/MyDocuments"));

const SKRBedomningContainer = lazy(() => import("./pages/SKRBedomningContainer"));
const WorkPreparationPage = lazy(() => import("./pages/WorkPreparationPage"));
const EntreprenorsLankar = lazy(() => import("./pages/EntreprenorsLankar"));
const Admin = lazy(() => import("./pages/Admin"));
const BackendStatus = lazy(() => import("./pages/BackendStatus"));
const Arbetsmiljoplan = lazy(() => import("./pages/Arbetsmiljoplan"));
const Arbetsomrade = lazy(() => import("./pages/Arbetsomrade"));
const Skyddsregler = lazy(() => import("./pages/Skyddsregler"));
const Samordning = lazy(() => import("./pages/Samordning"));
const SamRutin = lazy(() => import("./pages/SamRutin"));
const ProcessGuide = lazy(() => import("./pages/ProcessGuide"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const ProjectValueDashboard = lazy(() => import("./pages/ProjectValueDashboard"));
const DemoProjects = lazy(() => import("./pages/DemoProjects"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Hjalp = lazy(() => import("./pages/Hjalp"));
const Start = lazy(() => import("./pages/Start"));
const Guidning = lazy(() => import("./pages/Guidning"));
const GratisStart = lazy(() => import("./pages/GratisStart"));

const AnvandarLogg = lazy(() => import("./pages/AnvandarLogg"));

const ExternalDocument = lazy(() => import("./pages/ExternalDocument"));
const ExternalQuestionDispatch = lazy(() => import("./pages/ExternalQuestionDispatch"));
const ExternalWorkPrepInput = lazy(() => import("./pages/ExternalWorkPrepInput"));

const ContractorPortal = lazy(() => import("./pages/ContractorPortal"));
const VerifyDocument = lazy(() => import("./pages/VerifyDocument"));
const DeliveryPackage = lazy(() => import("./pages/DeliveryPackage"));
const Villkor = lazy(() => import("./pages/Villkor"));
const Integritetspolicy = lazy(() => import("./pages/Integritetspolicy"));

// Inloggat läge: alla appvyer kräver inloggning
const app = (element: React.ReactNode) => (
  <MainLayout>
    <ProtectedRoute>{element}</ProtectedRoute>
  </MainLayout>
);

// Define your routes - consolidated to use only UppdragPage system
export const router = createBrowserRouter([
  {
    path: "/",
    element: <MainLayout><Home /></MainLayout>,
  },
  {
    path: "/start",
    element: app(<Start />),
  },
  {
    path: "/guidning",
    element: app(<Guidning />),
  },
  {
    path: "/anvandarlogg",
    element: app(<AnvandarLogg />),
  },
  {
    path: "/profile",
    element: app(<Profile />),
  },
  {
    path: "/auth",
    element: <Auth />,
  },
  {
    path: "/dokument/extern/:token",
    element: <ExternalDocument />,
  },
  {
    path: "/projektorsvar/:token",
    element: <ExternalQuestionDispatch />,
  },

  {
    path: "/entreprenorsvar/:token",
    element: <ExternalWorkPrepInput />,
  },
  {
    path: "/entreprenor/:token",
    element: <ContractorPortal />,
  },
  {
    path: "/villkor",
    element: <MainLayout><Villkor /></MainLayout>,
  },
  {
    path: "/integritetspolicy",
    element: <MainLayout><Integritetspolicy /></MainLayout>,
  },
  {
    path: "/verifiera",
    element: <VerifyDocument />,
  },
  {
    path: "/verifiera/:code",
    element: <VerifyDocument />,
  },
  {
    path: "/leverans",
    element: <DeliveryPackage />,
  },
  {
    path: "/leverans/:code",
    element: <DeliveryPackage />,
  },
  {
    path: "/dokument",
    element: app(<MyDocuments />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/riskbedomning",
    element: app(<Riskbedomning />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/gratis-start",
    element: app(<GratisStart />),
  },
  {
    path: "/uppdrag",
    element: app(<Uppdrag />),
  },

  {
    path: "/uppdrag/:projektId",
    element: app(<Uppdrag />),
  },
  {
    path: "/uppdrag/:projektId/:folderId",
    element: app(<Uppdrag />),
  },
  {
    path: "/uppdrag/shared/:folderId",
    element: app(<Uppdrag />),
  },
  {
    path: "/document-settings",
    element: app(<DocumentSettingsPage />),
  },
  {
    path: "/edit-project/:projectId",
    element: app(<EditProjectPage />),
  },
  {
    path: "/translation-settings",
    element: app(<TranslationSettings />),
  },
  {
    path: "/skr-bedomning",
    element: app(<SKRBedomningContainer />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/skr-bedomning/:projektId/:folderId",
    element: app(<SKRBedomningContainer />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/entreprenorslankar/:projektId",
    element: app(<EntreprenorsLankar />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/arbetsberedning/:projektId/:folderId",
    element: app(<WorkPreparationPage />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/arbetsberedning/:projektId/:folderId/:workPreparationId",
    element: app(<WorkPreparationPage />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/amp/:projektId/:ampId",
    element: app(<Arbetsmiljoplan />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/arbetsomrade/:projektId",
    element: app(<Arbetsomrade />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/skyddsregler/:projektId",
    element: app(<Skyddsregler />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/samordning/:projektId",
    element: app(<Samordning />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/tillbud/:projektId",
    element: app(<Samordning />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/sam-rutin/:projektId",
    element: app(<SamRutin />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/processguide",
    element: app(<ProcessGuide />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/hjalp",
    element: app(<Hjalp />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/portfolj",
    element: app(
      <PlanGate
        required="pro"
        title="Portföljöversikt"
        description="Portföljöversikten samlar alla dina projekt på ett ställe – status, öppna tillbud och hur långt varje projekt kommit i Bas-processen. Den ingår i Pro."
        bullets={[
          "Se alla projekt och deras framdrift i en lista",
          "Fånga upp öppna tillbud och etapper utan Bas-U",
          "Gruppera projekt i program",
        ]}
      >
        <Portfolio />
      </PlanGate>
    ),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/processguide/:projektId",
    element: app(<ProcessGuide />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/projektvarde/:projectId",
    element: app(<ProjectValueDashboard />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/projektvarde",
    element: app(<ProjectValueDashboard />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/integrationer",
    element: app(
      <PlanGate
        required="pro"
        title="Integrationer"
        description="Med integrationer kan dokumenten ligga kvar i SharePoint eller OneDrive medan du jobbar i Byggarbetsmiljökontrollen. API-nycklar och webhooks ingår i Enterprise."
        bullets={[
          "Koppla projekt till SharePoint eller OneDrive",
          "Automatisk mappstruktur per fas eller roll",
          "API-nycklar och webhooks på Enterprise-nivå",
        ]}
      >
        <Integrations />
      </PlanGate>
    ),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/exempel",
    element: app(<DemoProjects />),
    errorElement: <MainLayout><NotFound /></MainLayout>,
  },
  {
    path: "/admin",
    element: app(<Admin />),
  },
  {
    path: "/backend-status",
    element: app(<BackendStatus />),
  },
  {
    path: "*",
    element: <MainLayout><NotFound /></MainLayout>,
  }
]);
